import React, { useEffect, useState } from "react";
import { Save, Check, Eye, ArrowUp, ArrowDown, Plus, Trash2, ExternalLink, QrCode, Image, Type, Grid3x3, Mail as MailIcon, Share2, Info, Layers } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const WIDGET_META = {
    hero: { label: "Hero / Copertina", icon: Image, color: "var(--mod-accent)" },
    offers: { label: "Griglia Offerte (3:2)", icon: Grid3x3, color: "var(--orange)" },
    text: { label: "Blocco Testo", icon: Type, color: "var(--cobalt)" },
    gallery: { label: "Galleria Foto", icon: Layers, color: "var(--yellow)" },
    contact: { label: "Form Contatti", icon: MailIcon, color: "var(--green)" },
    info: { label: "Info Contatti", icon: Info, color: "var(--navy)" },
    social: { label: "Icone Social", icon: Share2, color: "var(--cyan)" },
};

function Toggle({ checked, onChange }) {
    return (
        <button onClick={() => onChange(!checked)} type="button" className="w-10 h-6 rounded-full relative transition" style={{ background: checked ? "var(--mod-accent)" : "var(--line-2)" }}>
            <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition" style={{ left: checked ? "18px" : "2px" }}></span>
        </button>
    );
}

export default function Landing() {
    const [landing, setLanding] = useState(null);
    const [qr, setQr] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        (async () => {
            const [l, q] = await Promise.all([api.get("/tenant/landing"), api.get("/tenant/qr")]);
            setLanding(l.data);
            setQr(q.data);
        })();
    }, []);

    if (!landing) return <div className="text-center py-12" style={{ color: "var(--muted)" }}>Caricamento...</div>;

    const widgets = (landing.widgets || []).sort((a, b) => a.order - b.order);

    const updateWidget = (id, patch) => {
        setLanding(p => ({ ...p, widgets: p.widgets.map(w => w.id === id ? { ...w, ...patch } : w) }));
    };
    const updateData = (id, key, value) => {
        setLanding(p => ({ ...p, widgets: p.widgets.map(w => w.id === id ? { ...w, data: { ...w.data, [key]: value } } : w) }));
    };
    const moveWidget = (id, dir) => {
        const sorted = [...widgets];
        const idx = sorted.findIndex(w => w.id === id);
        const target = idx + dir;
        if (target < 0 || target >= sorted.length) return;
        [sorted[idx], sorted[target]] = [sorted[target], sorted[idx]];
        const renumbered = sorted.map((w, i) => ({ ...w, order: i + 1 }));
        setLanding(p => ({ ...p, widgets: renumbered }));
    };
    const updateOfferItem = (widgetId, itemIdx, key, value) => {
        setLanding(p => ({
            ...p,
            widgets: p.widgets.map(w => w.id !== widgetId ? w : {
                ...w, data: { ...w.data, items: w.data.items.map((it, i) => i === itemIdx ? { ...it, [key]: value } : it) }
            })
        }));
    };
    const addOffer = (widgetId) => updateData(widgetId, "items", [...(widgets.find(w=>w.id===widgetId).data.items || []), { title: "Nuova offerta", subtitle: "", price: "", image: "", link: "" }]);
    const delOffer = (widgetId, idx) => {
        const w = widgets.find(x => x.id === widgetId);
        updateData(widgetId, "items", w.data.items.filter((_, i) => i !== idx));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/tenant/landing", landing);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-5xl" data-testid="landing-editor">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        Landing della tua <span style={{ fontWeight: 600 }}>attività</span>
                    </h1>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>
                        Personalizza la vetrina che i tuoi clienti vedranno. Attiva/disattiva i widget, riordinali con le frecce.
                    </p>
                </div>
                <div className="flex gap-2">
                    {qr && (
                        <a href={qr.landing_url} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                            <Eye size={14} /> Anteprima
                        </a>
                    )}
                    <button data-testid="landing-save" onClick={save} disabled={saving} className="btn-grad px-5 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2">
                        {saving ? "Salvataggio..." : saved ? <><Check size={14} /> Salvato</> : <><Save size={14} /> Salva</>}
                    </button>
                </div>
            </div>

            {/* QR + Link share */}
            {qr && (
                <div className="card-tpl p-5 flex items-center gap-5 flex-wrap">
                    <img src={qr.qr_url} alt="QR" className="w-24 h-24 rounded-lg" />
                    <div className="flex-1 min-w-0">
                        <div className="mono text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--muted)" }}>URL pubblico della landing</div>
                        <div className="mono text-[12.5px] truncate font-bold" style={{ color: "var(--navy)" }}>{qr.landing_url}</div>
                        <p className="text-[12px] mt-1.5" style={{ color: "var(--text-2)" }}>
                            Condividi questo link/QR con i tuoi clienti: vedranno la tua vetrina e potranno contattarti.
                        </p>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(qr.landing_url); }} className="px-3 py-2 rounded-lg font-bold text-[12px] mono uppercase tracking-wider" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                        Copia link
                    </button>
                </div>
            )}

            {/* Brand customization */}
            <div className="card-tpl p-5">
                <h3 className="mono text-[10px] uppercase tracking-[0.12em] font-bold mb-3" style={{ color: "var(--navy)" }}>// Brand</h3>
                <div className="grid md:grid-cols-3 gap-3">
                    <div>
                        <label className="mono text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>Colore primario</label>
                        <input type="color" value={landing.primary_color || "#1E40AF"} onChange={(e) => setLanding(p => ({ ...p, primary_color: e.target.value }))} className="w-full h-10 rounded-lg cursor-pointer" />
                    </div>
                    <div>
                        <label className="mono text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>Colore accento</label>
                        <input type="color" value={landing.accent_color || "#FF1A5C"} onChange={(e) => setLanding(p => ({ ...p, accent_color: e.target.value }))} className="w-full h-10 rounded-lg cursor-pointer" />
                    </div>
                    <div>
                        <label className="mono text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>URL logo (opz.)</label>
                        <input value={landing.logo_url || ""} onChange={(e) => setLanding(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." className="input mt-1" />
                    </div>
                </div>
            </div>

            {/* Widgets list */}
            <div className="space-y-3">
                {widgets.map((w, idx) => {
                    const meta = WIDGET_META[w.type] || { label: w.type, icon: Info, color: "var(--muted)" };
                    const Icon = meta.icon;
                    return (
                        <div key={w.id} data-testid={`widget-${w.id}`} className="card-tpl p-4" style={{ opacity: w.enabled ? 1 : 0.55 }}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex flex-col gap-1">
                                    <button onClick={() => moveWidget(w.id, -1)} disabled={idx === 0} className="w-6 h-6 rounded grid place-items-center disabled:opacity-30" style={{ background: "var(--bg)" }}><ArrowUp size={11} /></button>
                                    <button onClick={() => moveWidget(w.id, 1)} disabled={idx === widgets.length - 1} className="w-6 h-6 rounded grid place-items-center disabled:opacity-30" style={{ background: "var(--bg)" }}><ArrowDown size={11} /></button>
                                </div>
                                <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "var(--mod-accent-soft)" }}>
                                    <Icon size={16} style={{ color: meta.color }} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-extrabold text-[14px]" style={{ color: "var(--navy)" }}>{meta.label}</div>
                                    <div className="mono text-[10px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>#{idx + 1}</div>
                                </div>
                                <Toggle checked={w.enabled} onChange={(v) => updateWidget(w.id, { enabled: v })} />
                            </div>

                            {w.enabled && (
                                <div className="pl-12 space-y-2">
                                    {(w.type === "hero" || w.type === "text" || w.type === "gallery" || w.type === "offers" || w.type === "contact") && (
                                        <>
                                            <input value={w.data.title || ""} onChange={(e) => updateData(w.id, "title", e.target.value)} placeholder="Titolo" className="input" />
                                            {(w.type === "hero" || w.type === "contact") && (
                                                <input value={w.data.subtitle || ""} onChange={(e) => updateData(w.id, "subtitle", e.target.value)} placeholder="Sottotitolo" className="input" />
                                            )}
                                            {w.type === "hero" && (
                                                <input value={w.data.image || ""} onChange={(e) => updateData(w.id, "image", e.target.value)} placeholder="URL immagine di copertina (opz.)" className="input" />
                                            )}
                                            {w.type === "text" && (
                                                <textarea rows={3} value={w.data.body || ""} onChange={(e) => updateData(w.id, "body", e.target.value)} placeholder="Testo del blocco" className="input" />
                                            )}
                                        </>
                                    )}

                                    {w.type === "offers" && (
                                        <div className="space-y-2 mt-2">
                                            {(w.data.items || []).map((it, i) => (
                                                <div key={i} className="grid grid-cols-12 gap-2 p-2 rounded-lg" style={{ background: "var(--bg)" }}>
                                                    <input value={it.title} onChange={(e) => updateOfferItem(w.id, i, "title", e.target.value)} placeholder="Titolo offerta" className="input col-span-4" />
                                                    <input value={it.subtitle} onChange={(e) => updateOfferItem(w.id, i, "subtitle", e.target.value)} placeholder="Descrizione breve" className="input col-span-4" />
                                                    <input value={it.price} onChange={(e) => updateOfferItem(w.id, i, "price", e.target.value)} placeholder="Prezzo" className="input col-span-2" />
                                                    <input value={it.image} onChange={(e) => updateOfferItem(w.id, i, "image", e.target.value)} placeholder="URL foto" className="input col-span-1" />
                                                    <button onClick={() => delOffer(w.id, i)} className="rounded-lg" style={{ color: "var(--red)" }}><Trash2 size={13} className="mx-auto" /></button>
                                                </div>
                                            ))}
                                            <button onClick={() => addOffer(w.id)} className="text-[12px] font-bold mt-2 flex items-center gap-1" style={{ color: "var(--cobalt)" }}>
                                                <Plus size={12} /> Aggiungi offerta
                                            </button>
                                        </div>
                                    )}

                                    {w.type === "social" && (
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                            {["facebook", "instagram", "whatsapp", "tiktok", "linkedin", "youtube"].map(s => (
                                                <input key={s} value={(landing.social || {})[s] || ""} onChange={(e) => setLanding(p => ({ ...p, social: { ...(p.social || {}), [s]: e.target.value } }))} placeholder={`URL ${s}`} className="input" />
                                            ))}
                                        </div>
                                    )}

                                    {w.type === "info" && (
                                        <div className="grid grid-cols-2 gap-2">
                                            {[["show_phone","Telefono"],["show_email","Email"],["show_address","Indirizzo"],["show_hours","Orari"]].map(([k,l]) => (
                                                <label key={k} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                                                    <input type="checkbox" checked={!!w.data[k]} onChange={(e) => updateData(w.id, k, e.target.checked)} style={{ accentColor: "var(--mod-accent)" }} /> {l}
                                                </label>
                                            ))}
                                            <input value={w.data.hours || ""} onChange={(e) => updateData(w.id, "hours", e.target.value)} placeholder="Orari (es. Lun-Ven 9-19)" className="input col-span-2" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
